import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Trophy, 
  Users, 
  Calendar,
  ArrowLeft,
  Crown,
  Medal,
  Target,
  Check,
  X,
  AlertTriangle
} from "lucide-react";

const TournamentDetailPage = ({ user, onLogout }) => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("matches");
  
  // Skor girişi
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [scoreInput, setScoreInput] = useState({ team1Score: 0, team2Score: 0 });

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      const [tournamentRes, standingsRes] = await Promise.all([
        axios.get(`${API}/tournaments/${tournamentId}`),
        axios.get(`${API}/tournaments/${tournamentId}/standings`)
      ]);
      
      setTournament(tournamentRes.data);
      setStandings(standingsRes.data?.standings || []);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
      toast.error("Turnuva bulunamadı");
    } finally {
      setLoading(false);
    }
  };

  const handleScoreClick = (match) => {
    setSelectedMatch(match);
    setScoreInput({ 
      team1Score: match.team1Score || 0, 
      team2Score: match.team2Score || 0 
    });
    setShowScoreDialog(true);
  };

  const handleSubmitScore = async () => {
    try {
      const response = await axios.post(
        `${API}/tournaments/${tournamentId}/matches/${selectedMatch.id}/score`,
        scoreInput
      );
      
      if (response.data.success) {
        toast.success("Skor kaydedildi");
        setShowScoreDialog(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Skor kaydedilemedi");
    }
  };

  const getMatchStatusBadge = (match) => {
    switch (match.status) {
      case "pending":
        return <Badge variant="outline">Oynanmadı</Badge>;
      case "score_entered":
        return <Badge className="bg-yellow-500">Skor Girildi</Badge>;
      case "confirmed":
        return <Badge className="bg-green-500">Onaylandı</Badge>;
      case "disputed":
        return <Badge className="bg-red-500">Uyuşmazlık</Badge>;
      default:
        return <Badge>{match.status}</Badge>;
    }
  };

  const isMyTeam = (teamId) => {
    if (!tournament || !user) return false;
    const team = tournament.teams?.find(t => t.id === teamId);
    return team?.captainId === user.id;
  };

  const canEnterScore = (match) => {
    if (match.status === "confirmed") return false;
    return isMyTeam(match.team1Id) || isMyTeam(match.team2Id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">Turnuva bulunamadı</p>
          <Button onClick={() => navigate("/tournaments")} className="mt-4">
            Turnuvalara Dön
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white">
        <div className="container mx-auto px-4 py-6">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate("/tournaments")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Turnuvalara Dön
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <Trophy className="h-8 w-8" />
                <h1 className="text-2xl font-bold">{tournament.name}</h1>
              </div>
              <p className="text-amber-100 mt-1">{tournament.description}</p>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <Badge className={tournament.type === "cup" ? "bg-amber-600" : "bg-blue-600"}>
                  {tournament.type === "cup" ? "🏆 Kupa" : "📊 Lig"}
                </Badge>
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {tournament.teams?.length || 0} Takım
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {new Date(tournament.startDate).toLocaleDateString('tr-TR')}
                </span>
              </div>
            </div>
            
            {tournament.winnerName && (
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 text-center">
                <Crown className="h-6 w-6 mx-auto text-yellow-300" />
                <p className="text-xs text-amber-100 mt-1">Şampiyon</p>
                <p className="font-bold">{tournament.winnerName}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="matches">
              <Target className="h-4 w-4 mr-2" />
              Maçlar
            </TabsTrigger>
            <TabsTrigger value="standings">
              <Trophy className="h-4 w-4 mr-2" />
              Puan Durumu
            </TabsTrigger>
            <TabsTrigger value="teams">
              <Users className="h-4 w-4 mr-2" />
              Takımlar
            </TabsTrigger>
          </TabsList>

          {/* Maçlar */}
          <TabsContent value="matches">
            <div className="space-y-4">
              {tournament.matches?.length === 0 ? (
                <Card className="text-center py-8">
                  <CardContent>
                    <p className="text-gray-500">Henüz maç oluşturulmamış</p>
                  </CardContent>
                </Card>
              ) : (
                tournament.matches?.map((match) => (
                  <Card key={match.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 mb-2">{match.roundName}</p>
                          <div className="flex items-center gap-4">
                            <div className={`flex-1 text-right ${match.winnerId === match.team1Id ? 'font-bold text-green-600' : ''}`}>
                              {match.team1Name}
                              {isMyTeam(match.team1Id) && <Badge className="ml-2 bg-blue-500">Sen</Badge>}
                            </div>
                            
                            <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg min-w-[80px] justify-center">
                              {match.status === "confirmed" ? (
                                <span className="font-bold text-lg">
                                  {match.team1Score} - {match.team2Score}
                                </span>
                              ) : match.status === "score_entered" ? (
                                <span className="text-yellow-600">
                                  {match.team1ScoreEntry ?? "?"} - {match.team2ScoreEntry ?? "?"}
                                </span>
                              ) : (
                                <span className="text-gray-400">vs</span>
                              )}
                            </div>
                            
                            <div className={`flex-1 ${match.winnerId === match.team2Id ? 'font-bold text-green-600' : ''}`}>
                              {match.team2Name}
                              {isMyTeam(match.team2Id) && <Badge className="ml-2 bg-blue-500">Sen</Badge>}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          {getMatchStatusBadge(match)}
                          {canEnterScore(match) && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleScoreClick(match)}
                            >
                              Skor Gir
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Puan Durumu */}
          <TabsContent value="standings">
            <Card>
              <CardHeader>
                <CardTitle>Puan Tablosu</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Takım</TableHead>
                      <TableHead className="text-center">O</TableHead>
                      <TableHead className="text-center">G</TableHead>
                      <TableHead className="text-center">B</TableHead>
                      <TableHead className="text-center">M</TableHead>
                      <TableHead className="text-center">A</TableHead>
                      <TableHead className="text-center">Y</TableHead>
                      <TableHead className="text-center">Av</TableHead>
                      <TableHead className="text-center font-bold">P</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {standings.map((team, idx) => (
                      <TableRow key={team.id} className={team.eliminated ? "opacity-50" : ""}>
                        <TableCell>
                          {idx === 0 && <Crown className="h-4 w-4 text-yellow-500 inline mr-1" />}
                          {idx === 1 && <Medal className="h-4 w-4 text-gray-400 inline mr-1" />}
                          {idx === 2 && <Medal className="h-4 w-4 text-amber-600 inline mr-1" />}
                          {idx + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          {team.name}
                          {team.eliminated && <Badge className="ml-2 bg-red-500">Elendi</Badge>}
                        </TableCell>
                        <TableCell className="text-center">{(team.wins || 0) + (team.draws || 0) + (team.losses || 0)}</TableCell>
                        <TableCell className="text-center text-green-600">{team.wins || 0}</TableCell>
                        <TableCell className="text-center text-gray-500">{team.draws || 0}</TableCell>
                        <TableCell className="text-center text-red-600">{team.losses || 0}</TableCell>
                        <TableCell className="text-center">{team.goalsFor || 0}</TableCell>
                        <TableCell className="text-center">{team.goalsAgainst || 0}</TableCell>
                        <TableCell className="text-center">{(team.goalsFor || 0) - (team.goalsAgainst || 0)}</TableCell>
                        <TableCell className="text-center font-bold">{team.points || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Takımlar */}
          <TabsContent value="teams">
            {tournament.teams?.length === 0 ? (
              <Card className="text-center py-8">
                <CardContent>
                  <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Henüz takım katılmamış</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {tournament.teams?.map((team, index) => (
                  <Card key={team.id} className="overflow-hidden">
                    <CardHeader className="pb-3 bg-gradient-to-r from-emerald-50 to-green-50">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {team.logo ? (
                            <img src={team.logo} alt="" className="w-12 h-12 rounded-full border-2 border-white shadow" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center border-2 border-white shadow">
                              <span className="text-white font-bold text-lg">{team.name?.charAt(0)}</span>
                            </div>
                          )}
                          <div className="absolute -top-1 -left-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                            {index + 1}
                          </div>
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg">{team.name}</CardTitle>
                          <CardDescription className="flex items-center gap-2">
                            <Crown className="h-3 w-3 text-amber-500" />
                            Kaptan: {team.captainName}
                          </CardDescription>
                        </div>
                        {team.clubId && (
                          <Badge variant="outline" className="bg-white">
                            Kulüp Takımı
                          </Badge>
                        )}
                        {team.isTemporary && (
                          <Badge variant="outline" className="bg-white text-purple-600 border-purple-300">
                            Geçici Takım
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Kadro ({team.players?.length || 0} oyuncu)
                        </p>
                        <div className="grid grid-cols-1 gap-2">
                          {team.players?.map((player, idx) => (
                            <div 
                              key={idx} 
                              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-medium text-emerald-700">
                                  {idx + 1}
                                </div>
                                <span className="text-sm font-medium">{player.name}</span>
                              </div>
                              {player.position && (
                                <Badge variant="secondary" className="text-xs">
                                  {player.position}
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Takım İstatistikleri - Eğer turnuva başladıysa */}
                      {tournament.status !== "registration" && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div className="bg-gray-50 rounded p-2">
                              <p className="text-xs text-gray-500">Maç</p>
                              <p className="font-bold">{team.played || 0}</p>
                            </div>
                            <div className="bg-green-50 rounded p-2">
                              <p className="text-xs text-gray-500">Galibiyet</p>
                              <p className="font-bold text-green-600">{team.wins || 0}</p>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <p className="text-xs text-gray-500">Beraberlik</p>
                              <p className="font-bold">{team.draws || 0}</p>
                            </div>
                            <div className="bg-red-50 rounded p-2">
                              <p className="text-xs text-gray-500">Mağlubiyet</p>
                              <p className="font-bold text-red-600">{team.losses || 0}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Skor Girişi Dialog */}
      <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Maç Skoru Gir</DialogTitle>
            <DialogDescription>
              Her iki takım da aynı skoru girerse otomatik onaylanır
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 text-right">
                <Label className="text-sm">{selectedMatch?.team1Name}</Label>
                <Input
                  type="number"
                  min="0"
                  value={scoreInput.team1Score}
                  onChange={(e) => setScoreInput({ ...scoreInput, team1Score: parseInt(e.target.value) || 0 })}
                  className="mt-1 text-center text-2xl font-bold"
                />
              </div>
              
              <span className="text-2xl text-gray-400">-</span>
              
              <div className="flex-1">
                <Label className="text-sm">{selectedMatch?.team2Name}</Label>
                <Input
                  type="number"
                  min="0"
                  value={scoreInput.team2Score}
                  onChange={(e) => setScoreInput({ ...scoreInput, team2Score: parseInt(e.target.value) || 0 })}
                  className="mt-1 text-center text-2xl font-bold"
                />
              </div>
            </div>
            
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                Dikkat: Girdiğiniz skor rakip takımın girdiği skorla eşleşmezse admin onayı gerekecektir.
              </p>
            </div>
            
            <Button className="w-full" onClick={handleSubmitScore}>
              <Check className="h-4 w-4 mr-2" />
              Skoru Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TournamentDetailPage;
